import React, { useEffect, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { createTimeLog, updateTimeLogs } from "../../../services/api/timeLog";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import BaseModal from "../../../components/ui/BaseModal";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import BaseButton from "../../../components/ui/BaseButton";
import { useParams } from "react-router-dom";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { displayFormattedTotalTimelog, handleGetLocation } from "../../../utils/helpers";
import useAuth from "../../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import UserProfileLink from "../../../components/ui/UserProfileLink";
dayjs.extend(utc);
dayjs.extend(timezone);
const TimeLogs = ({ refetchworkOrderPaymentData, mode }) => {
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const { authRole } = useAuth();
  const { id: workOrderId } = useParams();
  const dispatch = useDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const {
    isLoading: isCreateTimeLogLoading,
    error: isCreateTimeLogError,
    data: createdTimeLogData,
    mutateAsync: createTimeLogMutation,
  } = useMutation({
    mutationFn: createTimeLog,
    enabled: false,
  });
  const { updateWorkOrder } = useUpdateWorkOrder();
  const [isAddTimeModalOpen, setIsAddTimeModalOpen] = useState(false);
  const [timeLogFormData, setTimeLogFormData] = useState({
    id: null,
    checkInTime: null,
    checkInDate: null,
    checkOutTime: null,
    checkOutDate: null,
  });
  const {
    mutateAsync: updateTimeLogMutation,
    isLoading: isUpdatingTimeLog,
    error: updateTimeLogError,
  } = useMutation({
    mutationFn: updateTimeLogs,
  });
  const [newHours, setNewHours] = useState(0);
  const totalHours =
    workOrderDetail?.timeLogs?.reduce((acc, timeLog) => acc + +timeLog.totalHours, 0) || null;

  const handleChange = (name, e) => {
    setTimeLogFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: e,
      };

      if (
        updatedData.checkInDate &&
        updatedData.checkInTime &&
        updatedData.checkOutDate &&
        updatedData.checkOutTime
      ) {
        const checkInDateTime = dayjs(
          `${updatedData.checkInDate} ${updatedData.checkInTime}`,
          "YYYY-MM-DD HH:mm",
        );
        const checkOutDateTime = dayjs(
          `${updatedData.checkOutDate} ${updatedData.checkOutTime}`,
          "YYYY-MM-DD HH:mm",
        );
        const hoursDifference = checkOutDateTime.diff(checkInDateTime, "hour", true);
        setNewHours(hoursDifference);
      }

      return updatedData;
    });
  };
  const resetTimeLogForm = () => {
    setTimeLogFormData({
      checkInDate: null,
      checkInTime: null,
      checkOutDate: null,
      checkOutTime: null,
    });
    setNewHours(0);
  };
  const handleEdit = (logId) => {
    const log = workOrderDetail.timeLogs.find((l) => l.id === logId);
    setSelectedLog(log);

    setTimeLogFormData({
      id: log.id,
      checkInDate: dayjs.utc(log.checkInTime).tz(workOrderDetail?.timeZone).format("YYYY-MM-DD"),
      checkInTime: dayjs.utc(log.checkInTime).tz(workOrderDetail?.timeZone).format("HH:mm:ss"),
      checkOutDate: log.checkOutTime
        ? dayjs.utc(log.checkOutTime).tz(workOrderDetail?.timeZone).format("YYYY-MM-DD")
        : "",
      checkOutTime: log.checkOutTime
        ? dayjs.utc(log.checkOutTime).tz(workOrderDetail?.timeZone).format("HH:mm:ss")
        : "",
    });
    if (log.checkInTime && log.checkOutTime) {
      const tz = workOrderDetail?.timeZone;

      const checkIn = dayjs.utc(log.checkInTime).tz(tz);
      const checkOut = dayjs.utc(log.checkOutTime).tz(tz);

      const diff = checkOut.diff(checkIn, "hour", true);
      setNewHours(diff);
    } else {
      setNewHours(0);
    }

    setIsEditModalOpen(true);
  };

  const handleSaveButton = async () => {
    if (authRole !== "CLIENT") {
      dispatch(
        showToast({
          type: "error",
          message: "Only Work Order owner can add time.",
        }),
      );
      return;
    }
    const location = await handleGetLocation();
    try {
      let payload = {
        checkInTime: `${timeLogFormData.checkInDate} ${timeLogFormData.checkInTime}`,
        checkOutTime: `${timeLogFormData.checkOutDate} ${timeLogFormData.checkOutTime}`,
        longitude: location.longitude,
        latitude: location.latitude,
        workOrderId: parseInt(workOrderId),
      };
      await createTimeLogMutation(payload);
      updateWorkOrder();
      setIsAddTimeModalOpen(false);
      resetTimeLogForm();
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };
  const handleEditButton = async () => {
    if (authRole !== "CLIENT") {
      dispatch(
        showToast({
          type: "error",
          message: "Only Work Order owner can edit time.",
        }),
      );
      return;
    }
    if (!timeLogFormData.id) {
      dispatch(
        showToast({
          type: "error",
          message: "Cannot update: Invalid log ID",
        }),
      );
      return;
    }

    try {
      const logs = [];

      const log = {
        id: Number(timeLogFormData.id),
      };
      const tz = workOrderDetail?.timeZone || "Asia/Karachi";

      if (timeLogFormData.checkInDate && timeLogFormData.checkInTime) {
        log.checkInTime = dayjs
          .tz(`${timeLogFormData.checkInDate} ${timeLogFormData.checkInTime}`, tz)
          .toISOString();
      }

      if (timeLogFormData.checkOutDate && timeLogFormData.checkOutTime) {
        log.checkOutTime = dayjs
          .tz(`${timeLogFormData.checkOutDate} ${timeLogFormData.checkOutTime}`, tz)
          .toISOString();
      }
      logs.push(log);

      await updateTimeLogMutation({ logs: [log] });
      updateWorkOrder();
      refetchworkOrderPaymentData?.();
      setIsEditModalOpen(false);
      resetTimeLogForm();
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response?.data?.message || "Something went wrong",
        }),
      );
    }
  };

  return (
    <div className="p-4 sm:p-12">
      <div className="border-bottom mb-4  pb-1">
        <h6 className="text-dark">Time Log</h6>
      </div>

      <div>
        {totalHours && (
          <p className="font-normal mb-3">{displayFormattedTotalTimelog(totalHours?.toFixed(2))}</p>
        )}
        {workOrderDetail?.timeLogs?.map((timeLog) => (
          <TimeLog
            key={timeLog.id}
            timeLogId={timeLog.id}
            hours={timeLog.totalHours}
            checkInTime={timeLog.checkInTime}
            checkInDistance={timeLog.checkInDistance}
            checkInLoggedBy={`${timeLog.checkInLoggedBy?.firstName} ${timeLog.checkInLoggedBy?.lastName}`}
            checkInLoggedById={timeLog.checkInLoggedBy?.id}
            checkOutTime={timeLog.checkOutTime}
            checkOutDistance={timeLog.checkOutDistance}
            checkOutLoggedBy={`${timeLog.checkOutLoggedBy?.firstName} ${timeLog.checkOutLoggedBy?.lastName}`}
            checkOutLoggedById={timeLog.checkOutLoggedBy?.id}
            timeZone={workOrderDetail?.timeZone}
            onEdit={handleEdit}
            mode={mode}
          />
        ))}
      </div>

      {/* {authRole === "CLIENT" && (
        <div className="flex items-center mt-4 cursor-pointer">
          <i className="fa-solid fa-plus text-secondary"></i>
          <p
            className="text-secondary text-sm ml-1"
            onClick={() => setIsAddTimeModalOpen(true)}
          >
            {" "}
            Add Time
          </p>
        </div>
      )} */}

      <BaseModal open={isAddTimeModalOpen} close={() => setIsAddTimeModalOpen(true)}>
        <div style={{ padding: "24px 40px" }}>
          <h3
            style={{
              fontSize: "18px",
              paddingBottom: "8px",
              borderBottom: "1px solid #DFDFDF",
              color: "#2C2C2C",
            }}
          >
            Add Time Log
          </h3>
          <h3
            className="font-500"
            style={{
              margin: "14px 0px",
              fontSize: "18px",
              paddingBottom: "8px",
              color: "#686868",
            }}
          >
            {newHours && newHours > 0 ? newHours.toFixed(2) : "0.00"} Hours
          </h3>
          <div>
            <p>Check In</p>
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ width: "100%" }}>
                <BaseDatePicker
                  type="time"
                  name="checkInTime"
                  // value={dayjs(schedule.orderStartDate)}
                  onChange={(e) => handleChange("checkInTime", dayjs(e).format("HH:mm"))}
                />
              </div>
              <p
                style={{
                  margin: "0 14px",
                }}
              >
                on
              </p>
              <div style={{ width: "100%" }}>
                <BaseDatePicker
                  type="date"
                  name="checkInDate"
                  // value={dayjs(schedule.orderStartDate)}
                  onChange={(e) => handleChange("checkInDate", dayjs(e).format("YYYY-MM-DD"))}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: "20px" }}>
            <p>Check Out</p>
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ width: "100%" }}>
                <BaseDatePicker
                  type="time"
                  name="checkOutTime"
                  // value={dayjs(schedule.orderStartDate)}
                  onChange={(e) => handleChange("checkOutTime", dayjs(e).format("HH:mm"))}
                />
              </div>
              <p
                style={{
                  margin: "0 14px",
                }}
              >
                on
              </p>
              <div style={{ width: "100%" }}>
                <BaseDatePicker
                  type="date"
                  name="checkOutDate"
                  // value={dayjs(schedule.orderStartDate)}
                  onChange={(e) => handleChange("checkOutDate", dayjs(e).format("YYYY-MM-DD"))}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: "40px",
            padding: "24px 0",
            boxShadow: "0px 0px 14px 4px rgba(0, 0, 0, 0.07)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ marginRight: "12px" }}>
              <BaseButton
                style={{
                  fontWeight: "300",
                  fontSize: "14px",
                  padding: "5px 28px",
                }}
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsAddTimeModalOpen(false);
                  resetTimeLogForm();
                }}
              >
                Cancel
              </BaseButton>
            </div>
            <div style={{ marginLeft: "12px" }}>
              <BaseButton
                style={{
                  fontWeight: "300",
                  fontSize: "14px",
                  padding: "6px 30px",
                }}
                color="primary"
                onClick={handleSaveButton}
              >
                Save
              </BaseButton>
            </div>
          </div>
        </div>
      </BaseModal>
      <BaseModal
        open={isEditModalOpen}
        close={() => {
          setIsEditModalOpen(false);
          resetTimeLogForm();
        }}
      >
        <div className="px-4 sm:px-10 pt-3 sm:pt-6 pb-2 sm:pb-4">
          <h3
            style={{
              fontSize: "18px",
              paddingBottom: "8px",
              borderBottom: "1px solid #DFDFDF",
              color: "#2C2C2C",
            }}
          >
            Edit Time Log
          </h3>

          <h3
            className="font-500"
            style={{
              margin: "8px 0px",
              fontSize: "18px",
              paddingBottom: "4px",
              color: "#686868",
            }}
          >
            {newHours && newHours > 0 ? newHours.toFixed(2) : "0.00"} Hours
          </h3>

          {/* CHECK IN SECTION */}
          <div>
            <p>Check In</p>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-y-2 mt-2">
              <div className="w-full sm:flex-1 sm:min-w-0">
                <BaseDatePicker
                  type="time"
                  name="checkInTime"
                  readOnly={false}
                  value={
                    timeLogFormData.checkInDate && timeLogFormData.checkInTime
                      ? dayjs(`${timeLogFormData.checkInDate}T${timeLogFormData.checkInTime}`)
                      : null
                  }
                  onChange={(e) => handleChange("checkInTime", dayjs(e).format("HH:mm"))}
                />
              </div>

              <p className="shrink-0 mx-2 sm:mx-4">on</p>

              <div className="w-full sm:flex-1 sm:min-w-0">
                <BaseDatePicker
                  type="date"
                  name="checkInDate"
                  readOnly={false}
                  value={timeLogFormData.checkInDate ? dayjs(timeLogFormData.checkInDate) : null}
                  onChange={(e) => handleChange("checkInDate", dayjs(e).format("YYYY-MM-DD"))}
                />
              </div>
            </div>
          </div>

          {/* CHECK OUT SECTION */}
          <div className="mt-3 sm:mt-5">
            <p>Check Out</p>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-y-2 mt-2">
              <div className="w-full sm:flex-1 sm:min-w-0">
                <BaseDatePicker
                  type="time"
                  name="checkOutTime"
                  readOnly={false}
                  value={
                    timeLogFormData.checkOutDate && timeLogFormData.checkOutTime
                      ? dayjs(`${timeLogFormData.checkOutDate}T${timeLogFormData.checkOutTime}`)
                      : null
                  }
                  onChange={(e) => handleChange("checkOutTime", dayjs(e).format("HH:mm"))}
                />
              </div>

              <p className="shrink-0 mx-2 sm:mx-4">on</p>

              <div className="w-full sm:flex-1 sm:min-w-0">
                <BaseDatePicker
                  type="date"
                  name="checkOutDate"
                  readOnly={false}
                  value={timeLogFormData.checkOutDate ? dayjs(timeLogFormData.checkOutDate) : null}
                  onChange={(e) => handleChange("checkOutDate", dayjs(e).format("YYYY-MM-DD"))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER BUTTONS */}
        <div
          style={{
            marginTop: "16px",
            padding: "16px 0",
            boxShadow: "0px 0px 14px 4px rgba(0, 0, 0, 0.07)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ marginRight: "12px" }}>
              <BaseButton
                style={{
                  fontWeight: "300",
                  fontSize: "14px",
                  padding: "5px 28px",
                }}
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsEditModalOpen(false);
                  resetTimeLogForm();
                }}
              >
                Cancel
              </BaseButton>
            </div>

            <div style={{ marginLeft: "12px" }}>
              <BaseButton
                style={{
                  fontWeight: "300",
                  fontSize: "14px",
                  padding: "6px 30px",
                }}
                color="primary"
                onClick={handleEditButton}
              >
                Save
              </BaseButton>
            </div>
          </div>
        </div>
      </BaseModal>
    </div>
    // </BaseCard>
  );
};

function TimeLog({
  timeLogId,
  hours,
  checkInTime,
  checkInDistance,
  checkInLoggedBy,
  checkInLoggedById,
  checkOutTime,
  checkOutDistance,
  checkOutLoggedBy,
  checkOutLoggedById,
  timeZone,
  onEdit,
  mode,
}) {
  return (
    <div className="bg-[#F6F6F6] rounded-md py-2.5 px-6 mt-2 relative">
      {mode !== "view" && (
        <i
          className="fa-light fa-pencil text-sm text-[#747372] text-lg absolute right-4 top-2 cursor-pointer"
          onClick={() => onEdit(timeLogId)}
        ></i>
      )}
      {checkOutTime && (
        <p className="text-dark border-b w-full pb-1">
          {displayFormattedTotalTimelog(hours?.toFixed(2))}
        </p>
      )}
      <div className="grid grid-cols-7 items-center py-3">
        <div className="col-span-3">
          <p className="text-dark mb-1">
            {dayjs.utc(checkInTime).tz(timeZone).format("MM/DD/YYYY [at] h:mm A")}
          </p>
          <p className="text-xs mb-1">
            <span className="text-primary">{checkInDistance?.toFixed(2)} km</span>
            away from site
          </p>
          <p className="text-xs">
            Checked IN by{" "}
            <UserProfileLink
              as="span"
              userId={checkInLoggedById}
              search="?direct=true"
              className="text-primary"
              linkClassName="cursor-pointer hover:underline"
            >
              {checkInLoggedBy}
            </UserProfileLink>
          </p>
        </div>
        {checkOutTime && <i className="fa-regular fa-arrow-right-long text-2xl"></i>}
        {checkOutTime && (
          <div className="col-span-3">
            <p className="text-dark mb-1">
              {checkOutTime &&
                dayjs.utc(checkOutTime).tz(timeZone).format("MM/DD/YYYY [at] h:mm A")}
            </p>
            <p className="text-xs mb-1">
              <span className="text-primary">{checkOutDistance?.toFixed(2)} km</span>
              away from site
            </p>
            <p className="text-xs">
              Checked OUT by{" "}
              <UserProfileLink
                as="span"
                userId={checkOutLoggedById}
                search="?direct=true"
                className="text-primary"
                linkClassName="cursor-pointer hover:underline"
              >
                {checkOutLoggedBy}
              </UserProfileLink>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimeLogs;
